import { Loader2, Minus, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

/** Hours × techs × rate on a labor line (#1823 S3, migration 647). */
export interface LaborShape {
    hours: number;
    techs: number;
    /** $ per tech per hour. */
    rate: number;
}

/** Reads the labor columns off a line row (text or number), or null when it isn't a labor line. */
export function laborOf(row: {
    labor_hours?: string | number | null;
    labor_techs?: string | number | null;
    labor_rate?: string | number | null;
}): LaborShape | null {
    if (row.labor_techs == null || row.labor_hours == null) return null;
    const techs = Number(row.labor_techs);
    const hours = Number(row.labor_hours);
    if (!(techs >= 1) || !(hours > 0)) return null;
    return { hours, techs, rate: Number(row.labor_rate ?? 0) };
}

const money = (n: number) => `$${n.toFixed(2)}`;
const hoursLabel = (h: number) => `${Number.isInteger(h) ? h : h.toFixed(1).replace(/\.0$/, "")} h`;

/**
 * The techs stepper (#1823 S3, mockup B): `[− 3 h +] × [− 2 techs +] × $85 = 3 × $170`.
 *
 * Each tap saves right away — the server works out qty = hours and
 * unit = techs × rate and rewrites "Labor (N Techs)". Adding a tech keeps the
 * hours (Corbin 10/9: 3 h × 2 → 3 h × 3). The math is shown so the office can
 * check it against the customer's card at a glance.
 */
export function LaborCrewStepper({
    labor,
    onChange,
    disabled,
    pending,
    className,
}: {
    labor: LaborShape;
    onChange: (next: { labor_hours?: number; labor_techs?: number }) => void;
    disabled?: boolean;
    pending?: boolean;
    className?: string;
}) {
    const unit = Math.round(labor.techs * labor.rate * 100) / 100;
    const off = disabled || pending;
    const btn =
        "inline-flex h-8 w-8 items-center justify-center bg-secondary text-foreground hover:bg-accent disabled:cursor-not-allowed disabled:text-muted-foreground";
    return (
        <div
            className={cn("flex flex-wrap items-center gap-1.5 text-[12.5px] tabular-nums", className)}
            data-testid="labor-crew-stepper"
        >
            <span className="inline-flex items-center overflow-hidden rounded-md border border-border bg-card">
                <button
                    type="button"
                    className={btn}
                    aria-label="Less hours"
                    disabled={off || labor.hours <= 0.5}
                    onClick={() => onChange({ labor_hours: Math.max(0.5, labor.hours - 0.5) })}
                >
                    <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="min-w-[3.25rem] px-1 text-center font-semibold">{hoursLabel(labor.hours)}</span>
                <button
                    type="button"
                    className={btn}
                    aria-label="More hours"
                    disabled={off || labor.hours >= 100}
                    onClick={() => onChange({ labor_hours: Math.min(100, labor.hours + 0.5) })}
                >
                    <Plus className="h-3.5 w-3.5" />
                </button>
            </span>
            <span className="text-muted-foreground">×</span>
            <span className="inline-flex items-center overflow-hidden rounded-md border border-border bg-card">
                <button
                    type="button"
                    className={btn}
                    aria-label="Fewer techs"
                    disabled={off || labor.techs <= 1}
                    onClick={() => onChange({ labor_techs: labor.techs - 1 })}
                >
                    <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="min-w-[4.25rem] px-1 text-center font-semibold">
                    {labor.techs} tech{labor.techs === 1 ? "" : "s"}
                </span>
                <button
                    type="button"
                    className={btn}
                    aria-label="More techs"
                    disabled={off || labor.techs >= 10}
                    onClick={() => onChange({ labor_techs: labor.techs + 1 })}
                >
                    <Plus className="h-3.5 w-3.5" />
                </button>
            </span>
            <span className="text-muted-foreground">
                × {money(labor.rate)} = {hoursLabel(labor.hours).replace(" h", "")} × {money(unit)}
            </span>
            {pending && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-label="Saving" />}
        </div>
    );
}
